import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Bed, Users, Wifi, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback featured rooms in case API fails
const fallbackRooms = [
  {
    id: 'emerald-crystal-deluxe',
    name: 'Emerald Crystal Deluxe',
    description: 'Elegant deluxe room with king bed for two guests',
    image: '/images/gallery/hotel-4.jpeg',
    price: 2499,
    features: { bedType: 'King', guests: 2 },
  },
  {
    id: 'emerald-amber-hill-view',
    name: 'Emerald Amber Hill View',
    description: 'Premium room with stunning hill views',
    image: '/images/gallery/hotel-6.jpeg',
    price: 3999,
    features: { bedType: 'King', guests: 2 },
    featured: true,
  },
  {
    id: 'emerald-sapphire-suite',
    name: 'Emerald Sapphire Suite',
    description: 'Our finest suite with luxury amenities',
    image: '/images/gallery/hotel-34.jpeg',
    price: 6999,
    features: { bedType: 'King', guests: 2 },
    featured: true,
  },
];

export function RoomsPreview() {
  const [featuredRooms, setFeaturedRooms] = useState(fallbackRooms);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/rooms`);
        if (response.data && response.data.length > 0) {
          // Get first 3 rooms or featured ones, map to preview format
          const rooms = response.data.slice(0, 3).map(room => ({
            id: room.id,
            name: room.name,
            description: room.description,
            image: room.images?.[0] || '/images/gallery/hotel-4.jpeg',
            price: room.price,
            features: {
              bedType: room.features?.bedType || 'King',
              guests: room.features?.guests || 2
            },
            featured: room.featured || room.type === 'suite' || room.type === 'executive'
          }));
          setFeaturedRooms(rooms);
        }
      } catch (error) {
        console.error('Failed to fetch rooms for preview:', error);
        // Keep fallback rooms
      } finally {
        setLoading(false);
      }
    };
    fetchRooms();
  }, []);

  return (
    <section className="section-padding bg-background" data-testid="rooms-preview-section">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="overline text-emerald-700 dark:text-emerald-400 mb-3">Accommodations</p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
            Rooms & Suites
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Choose from our room categories starting at ₹2,499/night. Best rates guaranteed - lower than Booking.com & Expedia.
          </p>
        </motion.div>

        {/* Rooms Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featuredRooms.map((room, index) => (
            <motion.div
              key={room.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group"
              data-testid={`room-card-${room.id}`}
            >
              <Link to={`/rooms#${room.id}`} className="block">
                {/* Image Container */}
                <div className="relative overflow-hidden rounded-sm aspect-[4/3] mb-4">
                  <img
                    src={room.image}
                    alt={room.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-emerald-950/20 group-hover:bg-emerald-950/10 transition-colors duration-300" />
                  
                  {/* Price Badge */}
                  <div className="absolute top-4 right-4 bg-emerald-900/90 backdrop-blur-sm text-white px-3 py-1 text-sm">
                    <span className="text-gold">₹{room.price.toLocaleString()}</span>
                    <span className="text-emerald-200 text-xs">/night</span>
                  </div>
                  
                  {/* Featured Badge */}
                  {room.featured && (
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-gold text-emerald-950">
                        <Sparkles className="w-3 h-3 mr-1" />
                        Popular
                      </Badge>
                    </div>
                  )}
                </div>

                {/* Content */}
                <h3 className="font-serif text-xl text-foreground mb-2 group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-colors">
                  {room.name}
                </h3>
                <p className="text-muted-foreground text-sm mb-4">{room.description}</p>

                {/* Features */}
                <div className="flex items-center gap-4 text-muted-foreground text-sm">
                  <span className="flex items-center gap-1">
                    <Bed className="w-4 h-4" />
                    {room.features.bedType}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {room.features.guests} Guests
                  </span>
                  <Wifi className="w-4 h-4" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center mt-12"
        >
          <Link to="/rooms">
            <Button
              variant="outline"
              className="btn-gold"
              data-testid="view-all-rooms-btn"
            >
              View All Room Types
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
